import { createSpecStreamCompiler, type Spec } from '@json-render/core';
import { getLogger } from '@onefootprint/logger';
import { CURRENT_SCHEMA_VERSION, migrateSpec } from './migrations';

const { logWarn } = getLogger({ location: 'gen-ui-parser' });

export const repairJsonLine = (line: string): string => {
  const trimmed = line.trimEnd().replace(/,+$/, '');
  try {
    JSON.parse(trimmed);
    return trimmed;
  } catch {
    let inString = false;
    let isEscape = false;
    const closings: string[] = [];
    for (const ch of trimmed) {
      if (isEscape) {
        isEscape = false;
        continue;
      }
      if (ch === '\\' && inString) {
        isEscape = true;
        continue;
      }
      if (ch === '"') {
        inString = !inString;
        continue;
      }
      if (inString) continue;
      if (ch === '{') closings.push('}');
      else if (ch === '[') closings.push(']');
      else if (ch === '}' || ch === ']') closings.pop();
    }
    if (closings.length === 0) return trimmed;
    const repaired = trimmed + closings.reverse().join('');
    try {
      JSON.parse(repaired);
      return repaired;
    } catch {
      return trimmed;
    }
  }
};

export const sanitizeJsonl = (raw: string) => raw.split('\n').map(repairJsonLine).join('\n');

// json-render's resolveBindings calls Object.entries recursively on props,
// so null values (from AI output) must be stripped — undefined is safe, null is not.
export const stripNulls = (value: unknown): unknown => {
  if (value === null) return undefined;
  if (Array.isArray(value)) return value.map(stripNulls);
  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(Object.entries(value).flatMap(([k, v]) => (v === null ? [] : [[k, stripNulls(v)]])));
  }
  return value;
};

export const repairSpec = (spec: Spec): Spec => {
  if (!spec.root || !spec.elements) return spec;

  const elements = { ...spec.elements } as Record<string, { type: string; props: unknown; children: string[] }>;

  for (const id of Object.keys(elements)) {
    const el = elements[id];
    elements[id] = { ...el, props: el.props == null ? {} : (stripNulls(el.props) as Record<string, unknown>) };
  }

  for (const id of Object.keys(elements)) {
    const el = elements[id];
    if (Array.isArray(el.children)) {
      elements[id] = { ...el, children: el.children.filter(cid => Boolean(elements[cid])) };
    }
  }

  const reachable = new Set<string>();
  const queue = [spec.root];
  while (queue.length > 0) {
    const id = queue.shift()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    const el = elements[id];
    if (el && Array.isArray(el.children)) {
      for (const cid of el.children) queue.push(cid);
    }
  }

  const orphans = Object.keys(elements).filter(id => !reachable.has(id));
  if (orphans.length > 0) {
    logWarn('[GenUi Parser] orphan elements discarded (not reachable from root)', undefined, {
      orphanCount: orphans.length,
      orphanIds: orphans.join(','),
    });
    for (const id of orphans) delete elements[id];
  }

  return { ...spec, elements } as Spec;
};

export const parseAndMigrateSpec = (content: string, schemaVersion?: number): Spec | null => {
  try {
    const version = schemaVersion ?? 1;
    if (version > CURRENT_SCHEMA_VERSION) {
      logWarn('[GenUi Parser] spec schema is newer than supported; rendering without migrations', undefined, {
        version,
        supportedVersion: CURRENT_SCHEMA_VERSION,
      });
    }
    const compiler = createSpecStreamCompiler<Spec>();
    compiler.push(sanitizeJsonl(content));
    const raw = compiler.getResult();
    if (raw?.root && raw?.elements?.[raw.root]) {
      return migrateSpec(repairSpec(raw), version);
    }
  } catch (err) {
    console.error('[GenUi Parser] failed to parse block:', err);
  }
  return null;
};
